import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import {
  RefreshCw,
  Users,
  Clock,
  ChevronLeft,
  X,
  User,
  Mail,
  Phone,
  Calendar,
  CreditCard,
  Gift,
  MapPin,
  Gamepad2,
  Award,
  TrendingUp,
  Link2,
  Link2Off,
  Cake,
  AlertCircle,
  FileText,
  Star,
  Grid3X3,
  Crown,
  Zap,
  Lock,
} from 'lucide-react';
import PlayerDetailModal, { getTierColorFromDisplay, TierBadge } from '../components/PlayerDetailModal';

// Empty Seat Component
const EmptySeat = ({ seatNumber }) => (
  <div className="w-24 h-28 bg-gray-900 border-2 border-dashed border-gray-600 rounded-lg flex flex-col items-center justify-center">
    <span className="text-gray-500 text-xs uppercase">Seat {seatNumber}</span>
    <span className="text-gray-600 text-xs mt-1">Empty</span>
  </div>
);

// Player Seat Card Component
const PlayerSeatCard = ({ player, onClick }) => (
  <button
    onClick={onClick}
    className={`w-24 h-28 rounded-lg p-2 flex flex-col items-center justify-center relative transition-all hover:scale-105 hover:shadow-lg ${
      player.isBirthday 
        ? 'bg-amber-500/20 border-2 border-amber-500' 
        : player.isHighTier 
          ? 'bg-gray-700 border border-yellow-500/50' 
          : 'bg-gray-700 border border-gray-600'
    }`}
  >
    {/* Birthday badge */}
    {player.isBirthday && (
      <span className="absolute -top-2 -right-2 text-lg">{'\u{1F382}'}</span>
    )}
    {/* VIP badge */}
    {player.isHighTier && !player.isBirthday && (
      <span className="absolute -top-1 -left-1 bg-gray-800 rounded-full p-0.5">
        <Star size={10} className="text-yellow-500" fill="#EAB308" />
      </span>
    )}
    <span className="text-gray-400 text-[10px] uppercase shrink-0">Seat {player.SeatNumber}</span>
    <span className="text-white text-xs font-medium text-center truncate w-full mt-1 shrink-0">
      {player.displayName}
    </span>
    <span className="text-gray-400 text-[10px] mt-0.5 shrink-0">{player.timeSeated}</span>
    {player.avgBetSource === 'live' ? (
      <span
        className="text-[11px] leading-none font-bold mt-0.5 px-1.5 py-0.5 rounded shrink-0 bg-emerald-500/25 text-emerald-200"
        title="Live rated average bet (current session)"
      >
        {`$${player.avgBet}`}
      </span>
    ) : player.avgBetSource === 'none' ? (
      <span
        className="text-[11px] leading-none font-bold mt-0.5 px-1.5 py-0.5 rounded shrink-0 bg-amber-500/25 text-amber-200 ring-1 ring-amber-400/40"
        title="No average bet entered — floor has not rated this player"
      >
        No avg
      </span>
    ) : null}
    {player.tierDisplay ? (
      <TierBadge tier={player.tierDisplay} color={getTierColorFromDisplay(player.tierDisplay)} small />
    ) : (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium border border-gray-500 text-gray-400 bg-gray-500/10 shrink-0">
        Guest
      </span>
    )}
  </button>
);

// Poker Table Visualization Component
const PokerTableView = ({ tableName, gameName, maxSeats, seated, onPlayerPress }) => {
  // Create array of all seats (filled or empty)
  const getSeats = () => {
    const seatCount = maxSeats || 9;
    const seats = Array(seatCount).fill(null);
    seated.forEach(player => {
      if (player.SeatNumber > 0 && player.SeatNumber <= seatCount) {
        seats[player.SeatNumber - 1] = player;
      }
    });
    return seats;
  };

  const seats = getSeats();
  const seatCount = maxSeats || 9;
  
  // Distribute seats around the table based on seat count
  // For 9 seats: top (2), right (2), bottom (3), left (2)
  // For 10 seats: top (2), right (3), bottom (3), left (2)
  let topSeats, rightSeats, bottomSeats, leftSeats;
  
  if (seatCount <= 6) {
    // Smaller table: top (1), right (1), bottom (2), left (2)
    topSeats = seats.slice(0, 1);
    rightSeats = seats.slice(1, 2);
    bottomSeats = seats.slice(2, 4).reverse();
    leftSeats = seats.slice(4, 6).reverse();
  } else if (seatCount <= 8) {
    // 8 seat table: top (2), right (2), bottom (2), left (2)
    topSeats = seats.slice(0, 2);
    rightSeats = seats.slice(2, 4);
    bottomSeats = seats.slice(4, 6).reverse();
    leftSeats = seats.slice(6, 8).reverse();
  } else {
    // 9-10 seat table: top (2), right (2), bottom (3), left (2)
    topSeats = seats.slice(0, 2);
    rightSeats = seats.slice(2, 4);
    bottomSeats = seats.slice(4, 7).reverse();
    leftSeats = seats.slice(7, seatCount).reverse();
  }

  const renderSeat = (player, seatNum) => {
    if (!player) {
      return <EmptySeat key={`empty-${seatNum}`} seatNumber={seatNum} />;
    }
    return (
      <PlayerSeatCard
        key={player.SeatedId}
        player={player}
        onClick={() => onPlayerPress(player)}
      />
    );
  };

  return (
    <div className="flex flex-col items-center py-8">
      <h2 className="text-xl font-bold text-amber-500 mb-6">{gameName}</h2>
      
      {/* Top row */}
      <div className="flex gap-4 mb-4">
        {topSeats.map((p, i) => renderSeat(p, i + 1))}
      </div>

      {/* Middle section */}
      <div className="flex items-center gap-4">
        {/* Left seats */}
        <div className="flex flex-col gap-4">
          {leftSeats.map((p, i) => renderSeat(p, seatCount - i))}
        </div>

        {/* The table */}
        <div className="w-48 h-32 bg-green-700 rounded-[50%] border-4 border-gray-800 flex flex-col items-center justify-center shadow-xl">
          <span className="text-white font-semibold">Table {tableName}</span>
          <span className="text-green-200 text-sm mt-1">
            {seated.length}/{seatCount} seated
          </span>
        </div>

        {/* Right seats */}
        <div className="flex flex-col gap-4">
          {rightSeats.map((p, i) => renderSeat(p, i + 3))}
        </div>
      </div>

      {/* Bottom row */}
      <div className="flex gap-4 mt-4">
        {bottomSeats.map((p, i) => renderSeat(p, 4 + bottomSeats.length - 1 - i + 1))}
      </div>
    </div>
  );
};


// Main Table Manager Component
export default function TableManager() {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [showPlayerPanel, setShowPlayerPanel] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem('tableManagerAutoRefresh');
    return saved === 'true';
  });

  // Persist autoRefresh to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('tableManagerAutoRefresh', autoRefresh.toString());
  }, [autoRefresh]);

  const fetchTables = async () => {
    try {
      const response = await api.get('/admin/table-manager/tables');
      setTables(response.data.tables || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching tables:', err);
      setError(err.response?.data?.error || 'Failed to load tables');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchTableDetails = async (tableName) => {
    setLoadingTable(true);
    try {
      const response = await api.get(`/admin/table-manager/tables/${encodeURIComponent(tableName)}`);
      setSelectedTable(response.data);
    } catch (err) {
      console.error('Error fetching table details:', err);
      setError(err.response?.data?.error || 'Failed to load table details');
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, []);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      if (selectedTable) {
        fetchTableDetails(selectedTable.table.TableName);
      }
      fetchTables();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, selectedTable]);

  const handleRefresh = () => {
    setRefreshing(true);
    if (selectedTable) {
      fetchTableDetails(selectedTable.table.TableName);
    }
    fetchTables();
  };

  const handleTableSelect = (table) => {
    fetchTableDetails(table.TableName);
  };

  const handlePlayerPress = (player) => {
    setSelectedPlayer(player);
    setShowPlayerPanel(true);
  };

  const handleClosePlayerPanel = () => {
    setShowPlayerPanel(false);
    setSelectedPlayer(null);
  };

  const handleBackToList = () => {
    setSelectedTable(null);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500 mb-4"></div>
        <p className="text-gray-400">Loading tables...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {selectedTable && (
            <button
              onClick={handleBackToList}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
          )}
          <h1 className="text-2xl font-bold text-white">
            {selectedTable ? `Table ${selectedTable.table.TableName}` : 'Table Manager'}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {/* Auto-refresh toggle */}
          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-amber-500 focus:ring-amber-500"
            />
            Auto-refresh
          </label>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="text-red-400" size={20} />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {selectedTable ? (
        // Table Detail View
        <div className="bg-gray-800 rounded-xl p-6">
          {loadingTable ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500 mb-4"></div>
              <p className="text-gray-400">Loading table...</p>
            </div>
          ) : (
            <PokerTableView
              tableName={selectedTable.table.TableName}
              gameName={selectedTable.table.GameName}
              maxSeats={selectedTable.table.MaxPlayers || selectedTable.table.MaxSeats || 9}
              seated={selectedTable.seated}
              onPlayerPress={handlePlayerPress}
            />
          )}
        </div>
      ) : (
        // Table List View - Grouped by Game
        <div className="space-y-6">
          <p className="text-gray-400 flex items-center gap-2">
            <Grid3X3 size={16} />
            Active Tables ({tables.length})
          </p>

          {tables.length === 0 ? (
            <div className="bg-gray-800 rounded-xl p-12 flex flex-col items-center justify-center">
              <Grid3X3 className="text-gray-600 mb-4" size={48} />
              <p className="text-gray-400 text-lg">No active tables</p>
              <p className="text-gray-500 text-sm mt-1">Tables with seated players will appear here</p>
            </div>
          ) : (
            // Group tables by game name
            (() => {
              const groupedTables = tables.reduce((acc, table) => {
                const gameName = table.GameName || 'Unknown Game';
                if (!acc[gameName]) {
                  acc[gameName] = [];
                }
                acc[gameName].push(table);
                return acc;
              }, {});

              // Sort game names alphabetically
              const sortedGameNames = Object.keys(groupedTables).sort();

              return (
                <div className="space-y-6">
                  {sortedGameNames.map((gameName) => {
                    const gameTables = groupedTables[gameName];
                    const totalSeated = gameTables.reduce((sum, t) => sum + (t.SeatedCount || 0), 0);
                    const totalCapacity = gameTables.reduce((sum, t) => sum + (t.MaxSeats || 9), 0);

                    return (
                      <div key={gameName} className="space-y-3">
                        {/* Game Header */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
                              <Gamepad2 className="text-amber-500" size={16} />
                            </div>
                            <div>
                              <h3 className="text-white font-semibold">{gameName}</h3>
                              <p className="text-gray-500 text-xs">
                                {gameTables.length} table{gameTables.length !== 1 ? 's' : ''} â€¢ {totalSeated}/{totalCapacity} seated
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Tables Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-11">
                          {gameTables
                            .sort((a, b) => a.TableName.localeCompare(b.TableName, undefined, { numeric: true }))
                            .map((table) => (
                              <button
                                key={table.TableId}
                                onClick={() => handleTableSelect(table)}
                                className="bg-gray-800 rounded-xl p-6 text-left hover:bg-gray-750 hover:ring-2 hover:ring-amber-500/50 transition-all group"
                              >
                                <div className="flex items-start justify-between mb-4">
                                  <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                                    <Grid3X3 className="text-amber-500" size={24} />
                                  </div>
                                  <div className="text-right">
                                    <span className="text-2xl font-bold text-amber-500">
                                      {table.SeatedCount}
                                    </span>
                                    <span className="text-gray-500">/{table.MaxSeats || 9}</span>
                                    <p className="text-xs text-gray-500">seated</p>
                                  </div>
                                </div>
                                <h3 className="text-lg font-semibold text-white mb-1">
                                  Table {table.TableName}
                                </h3>
                                {table.SmallBlind && table.BigBlind && (
                                  <p className="text-gray-500 text-xs mt-1">
                                    ${table.SmallBlind}/${table.BigBlind}
                                  </p>
                                )}
                              </button>
                            ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          )}
        </div>
      )}

      {/* Player Stats Panel */}
      <PlayerDetailModal
        visible={showPlayerPanel}
        onClose={handleClosePlayerPanel}
        playerId={selectedPlayer?.PlayerId}
        player={selectedPlayer}
      />
    </div>
  );
}
